import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Check, Circle } from "lucide-react";
import { Alert, Button, TextInput, useToast } from "@/ui";
import { AuthCard, AuthLayout } from "@/layout/AuthLayout";
import { useTenant } from "@/tenant/TenantProvider";
import { api, ApiClientError } from "@/lib/api";

/**
 * Consumes the link sent by `ForgotPassword` (security audit item 5).
 * Deliberately does not sign the user in on success — a fresh password is
 * not proof of anything else about a session — so this always redirects to
 * a normal sign-in instead.
 */
export function ResetPassword() {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();
  const toast = useToast();
  const { error: tenantError, loading: tenantLoading } = useTenant();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const loginPath = orgSlug ? `/${orgSlug}/login` : "/login";

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFields({});
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password, confirm });
      toast.success("Password updated. Please sign in.");
      navigate(loginPath, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const longEnough = password.length >= 8;
  const matches = confirm.length > 0 && password === confirm;

  return (
    <AuthLayout
      accent="new password."
      blurb="Choose a new password to finish resetting your account."
      headline="Set your"
      points={[
        "This link works once",
        "You'll be signed out of every other device",
        "Then you can sign in as usual",
      ]}
    >
      <AuthCard
        footer={
          <>
            Back to{" "}
            <Link className="font-medium text-brand-ink underline" to={loginPath}>
              sign in
            </Link>
          </>
        }
        subtitle="Choose a new password"
        title="Reset your password"
      >
        {tenantError && !tenantLoading ? (
          <Alert kind="danger">{tenantError}</Alert>
        ) : !token ? (
          <Alert kind="danger">
            This link is missing its reset code. Please request a new one from the sign-in page.
          </Alert>
        ) : (
          <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <TextInput
                autoComplete="new-password"
                autoFocus
                error={fields.password}
                label="New password"
                onChange={(e) => setPassword(e.target.value)}
                required
                type="password"
                value={password}
              />
              <ul className="flex flex-col gap-0.5 text-sm">
                <li className={`flex items-center gap-1.5 ${longEnough ? "text-success" : "text-muted"}`}>
                  {longEnough ? (
                    <Check aria-hidden="true" className="size-4" />
                  ) : (
                    <Circle aria-hidden="true" className="size-4" />
                  )}
                  At least 8 characters
                </li>
              </ul>
            </div>

            <TextInput
              autoComplete="new-password"
              error={fields.confirm}
              label="Confirm password"
              onChange={(e) => setConfirm(e.target.value)}
              required
              type="password"
              value={confirm}
            />

            {fields.token ? <Alert kind="danger">{fields.token}</Alert> : null}

            <Button block disabled={!longEnough || !matches} loading={submitting} type="submit">
              Reset password
            </Button>
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
